import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, Shield, DollarSign, Clock, Users, Building, PiggyBank, BarChart3 } from "lucide-react";
import Navigation from "@/components/Navigation";

const WhyInvestInLandContracts = () => {
  const landContractBenefits = [
    {
      icon: <TrendingUp className="h-8 w-8 text-green-600" />,
      title: "High Returns",
      description: "Earn 8-12% annual returns, significantly higher than traditional investments.",
      details: "Land contracts typically offer returns of 8-12% annually, providing substantial income compared to savings accounts, CDs, or bonds."
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-600" />,
      title: "Asset-Backed Security",
      description: "Your investment is secured by real estate, providing tangible collateral.",
      details: "Unlike stocks or bonds, land contracts are backed by actual property, giving you a physical asset as security for your investment."
    },
    {
      icon: <DollarSign className="h-8 w-8 text-green-600" />,
      title: "Monthly Cash Flow",
      description: "Receive consistent monthly payments from land contract purchasers.",
      details: "Land contracts provide predictable monthly income streams, helping you plan your finances with confidence."
    },
    {
      icon: <Clock className="h-8 w-8 text-purple-600" />,
      title: "Passive Income",
      description: "Earn money without active management or daily oversight required.",
      details: "Once your investment is made, land contracts require minimal ongoing management while generating steady returns."
    },
    {
      icon: <Users className="h-8 w-8 text-orange-600" />,
      title: "Helping Homeowners",
      description: "Support families in achieving homeownership while earning returns.",
      details: "Land contracts help families who might not qualify for traditional mortgages to purchase homes, creating a win-win situation."
    },
    {
      icon: <Building className="h-8 w-8 text-indigo-600" />,
      title: "Diversification",
      description: "Add real estate exposure to your portfolio without property management.",
      details: "Land contracts offer real estate investment benefits without the hassles of being a landlord or property manager."
    }
  ];

  const comparisons = [
    {
      category: "Annual Returns",
      landContracts: "8-12%",
      stocks: "7-10% (volatile)",
      bonds: "2-4%",
      savings: "0.5-1%",
      realEstate: "6-8% + management"
    },
    {
      category: "Risk Level",
      landContracts: "Moderate (asset-backed)",
      stocks: "High (market volatility)",
      bonds: "Low-Moderate",
      savings: "Very Low",
      realEstate: "Moderate-High"
    },
    {
      category: "Liquidity",
      landContracts: "Moderate (can be sold)",
      stocks: "High (daily trading)",
      bonds: "Moderate",
      savings: "High",
      realEstate: "Low (time to sell)"
    },
    {
      category: "Management Required",
      landContracts: "None",
      stocks: "Research & monitoring",
      bonds: "Minimal",
      savings: "None",
      realEstate: "Significant"
    },
    {
      category: "Income Frequency",
      landContracts: "Monthly",
      stocks: "Quarterly (dividends)",
      bonds: "Semi-annual",
      savings: "Monthly",
      realEstate: "Monthly (rent)"
    },
    {
      category: "Minimum Investment",
      landContracts: "$15,000+",
      stocks: "$1+",
      bonds: "$1,000+",
      savings: "$1+",
      realEstate: "$50,000+"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navigation />
      
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Real Estate's Best Kept Secret
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover the compelling advantages of land contract investments and see how they compare 
              to traditional investment strategies. Build wealth with asset-backed security and consistent returns.
            </p>
          </div>

          {/* Benefits Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Why Land Contracts Are Real Estate's Best Kept Secret
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {landContractBenefits.map((benefit, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      {benefit.icon}
                      <CardTitle className="text-xl">{benefit.title}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {benefit.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{benefit.details}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Investment Strategy Comparison</CardTitle>
              <CardDescription className="text-center text-lg">
                See how land contracts stack up against other popular investment options
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left p-4 font-semibold text-gray-900">Investment Type</th>
                      <th className="text-center p-4 font-semibold text-green-700 bg-green-50">Land Contracts</th>
                      <th className="text-center p-4 font-semibold text-gray-700">Stock Market</th>
                      <th className="text-center p-4 font-semibold text-gray-700">Bonds</th>
                      <th className="text-center p-4 font-semibold text-gray-700">Savings Account</th>
                      <th className="text-center p-4 font-semibold text-gray-700">Rental Real Estate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisons.map((row, index) => (
                      <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                        <td className="p-4 font-medium text-gray-900">{row.category}</td>
                        <td className="p-4 text-center font-semibold text-green-700 bg-green-50">{row.landContracts}</td>
                        <td className="p-4 text-center text-gray-700">{row.stocks}</td>
                        <td className="p-4 text-center text-gray-700">{row.bonds}</td>
                        <td className="p-4 text-center text-gray-700">{row.savings}</td>
                        <td className="p-4 text-center text-gray-700">{row.realEstate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Call to Action */}
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-gray-900">Ready to Discover the Secret?</CardTitle>
              <CardDescription className="text-lg">
                Join our platform and discover vetted land contract opportunities that can enhance your investment portfolio
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="flex flex-col items-center">
                  <PiggyBank className="h-12 w-12 text-green-600 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Low Minimum Investment</h4>
                  <p className="text-gray-600">Start with as little as $15,000</p>
                </div>
                <div className="flex flex-col items-center">
                  <Shield className="h-12 w-12 text-blue-600 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Thorough Due Diligence</h4>
                  <p className="text-gray-600">Every investment is carefully vetted</p>
                </div>
                <div className="flex flex-col items-center">
                  <BarChart3 className="h-12 w-12 text-purple-600 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Consistent Returns</h4>
                  <p className="text-gray-600">Steady monthly cash flow</p>
                </div>
              </div>
              <div className="space-y-4">
                <p className="text-xl font-semibold text-gray-900">
                  Contact us today to learn more about available opportunities
                </p>
                <p className="text-gray-600">
                  Our team will help you find the right land contract investments to meet your financial goals
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WhyInvestInLandContracts;
